import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import type { SuggestedIndustriesMap } from "app/pages/sales-intelligence/domain/industry/types/industry";

import type { LegacyIndustryCategoriesById } from "../models/LegacyIndustryFilter/types";
import industryFilterTrackingService from "../services/tracking/industry-filter/trackingService";
import type { ChipItemProps, SelectStrategy, SelectStrategyDeps } from "../types/industrySuggestions";

/** @deprecated Flat-list selection for the legacy industry filter. Superseded by the taxonomy algebra (SI-8780). */
export const makeFlatSelectStrategy = (deps: SelectStrategyDeps): SelectStrategy => {
    const { value, industries, categoriesById, onFilterUpdate, onChipRemove, inclusion } = deps;

    const getChipItems = (): ChipItemProps[] => {
        return value.selectedIndustries.reduce((items: ChipItemProps[], id: string): ChipItemProps[] => {
            const category = categoriesById[id];

            if (!category) {
                return items;
            }

            items.push({
                id: category.id,
                text: category.text,
                icon: category.icon,
                onCloseItem: () => onChipRemove([id]),
            });

            return items;
        }, []);
    };

    const onSelect = (industry?: IFlattenedCategory) => {
        if (!industry) {
            return;
        }

        if (value.selectedIndustries.includes(industry.id)) {
            industryFilterTrackingService.trackIndustryRemove(industry.id);
            onFilterUpdate({
                ...value,
                selectedIndustries: value.selectedIndustries.filter((id) => id !== industry.id),
                inclusion,
            });

            return;
        }

        industryFilterTrackingService.trackIndustryAdd(industry.id);
        onFilterUpdate({ ...value, selectedIndustries: value.selectedIndustries.concat(industry.id), inclusion });
    };

    const getItems = (searchTerm: string): readonly IFlattenedCategory[] => {
        const normalizedTerm = searchTerm.trim().toLowerCase();
        const searchTokens = normalizedTerm.split(" ").filter(Boolean);

        const minMatchIndex = (string: string) => {
            const words = string.split(" ").map((w) => w.toLowerCase());

            const allTokensMatch = searchTokens.every((token) => words.some((word) => word.indexOf(token) !== -1));

            if (!allTokensMatch) {
                return Infinity;
            }

            return Math.min(
                ...words.map((word) => {
                    const index = word.indexOf(searchTokens[0]);
                    return index === -1 ? Infinity : index;
                }),
            );
        };

        let items = [...industries].sort((a, b) => a.text.localeCompare(b.text));

        if (normalizedTerm) {
            items = items
                .filter((item) => minMatchIndex(item.text) !== Infinity)
                .sort((a, b) => Number(a.isChild) - Number(b.isChild))
                .sort((a, b) => minMatchIndex(a.text) - minMatchIndex(b.text));
        }

        return items;
    };

    const getSelectedItemIds = (): readonly string[] => value.selectedIndustries;

    return { getChipItems, onSelect, getItems, getSelectedItemIds };
};

const MAX_SUGGESTIONS = 4;

export function getSuggestions(
    selectedIds: readonly string[],
    categoriesById: LegacyIndustryCategoriesById,
    ignoredIndustries: readonly string[],
    suggestedIndustries: SuggestedIndustriesMap,
): readonly string[] {
    const { selectedChildIds, selectedParentIds } = selectedIds.reduce<{
        selectedChildIds: string[];
        selectedParentIds: string[];
    }>(
        (grouped, id) => {
            const industry = categoriesById[id];

            // A selected id the category list does not know — a deep link, or a saved value whose category
            // was renamed upstream. Every other lookup here already skips it; unguarded, this one throws
            // during the card's first render and takes the whole filters panel down.
            if (!industry) {
                return grouped;
            }

            if (industry.isChild) {
                grouped.selectedChildIds.push(id);
            } else {
                grouped.selectedParentIds.push(id);
            }

            return grouped;
        },
        { selectedChildIds: [], selectedParentIds: [] },
    );

    function makeSuggestionsMap(
        industryIdsToFindSuggestionsFor: readonly string[],
        suggestionsRecord: SuggestedIndustriesMap,
    ) {
        return industryIdsToFindSuggestionsFor.reduce((suggestionsMap, industryId) => {
            const rawSuggestions = suggestionsRecord[industryId] ?? [];

            const suggestions = rawSuggestions
                .filter((suggestion) => {
                    const suggestionsParentId = categoriesById[suggestion.suggestedSubIndustry]?.parentItem?.id;

                    const isSuggestionSelected = selectedChildIds.includes(suggestion.suggestedSubIndustry);
                    const isParentSelected = !!suggestionsParentId && selectedParentIds.includes(suggestionsParentId);
                    const isIgnored = ignoredIndustries.includes(suggestion.suggestedSubIndustry);

                    return !isSuggestionSelected && !isParentSelected && !isIgnored;
                })
                .sort((a, b) => b.jScore - a.jScore)
                .map((suggestion) => suggestion.suggestedSubIndustry);

            suggestionsMap.set(industryId, suggestions);

            return suggestionsMap;
        }, new Map<string, string[]>());
    }

    function fillWithSuggestionsFromMap(suggestions: string[], suggestionsMap: Map<string, string[]>) {
        let suggestionsLeft = Array.from(suggestionsMap.values()).reduce((count, arr) => count + arr.length, 0);
        let suggestionsIterator = suggestionsMap.values();
        while (suggestions.length < MAX_SUGGESTIONS && suggestionsLeft > 0) {
            const iteratorResult = suggestionsIterator.next();
            if (iteratorResult.done) {
                suggestionsIterator = suggestionsMap.values();
                continue;
            }

            const suggestionsPortion = iteratorResult.value;
            while (suggestionsPortion.length) {
                const suggestion = suggestionsPortion.shift();
                suggestionsLeft--;
                if (!suggestion || suggestions.includes(suggestion)) {
                    continue;
                }

                suggestions.push(suggestion);
                break;
            }
        }
    }

    const suggestedParentIndustryIds = selectedChildIds.reduce<string[]>((industryIds, selectedId) => {
        const parentIndustryId = categoriesById[selectedId].parentItem?.id;

        const shouldAddParentIndustryId =
            parentIndustryId &&
            !selectedParentIds.includes(parentIndustryId) &&
            !industryIds.includes(parentIndustryId) &&
            !ignoredIndustries.includes(parentIndustryId);

        if (shouldAddParentIndustryId) {
            industryIds.push(parentIndustryId);
        }

        return industryIds;
    }, []);

    const suggestionsForSubIndustries = makeSuggestionsMap(selectedChildIds, suggestedIndustries ?? {});

    const industriesSuggestions = selectedParentIds.reduce<SuggestedIndustriesMap>((record, parentId) => {
        const industry = categoriesById[parentId];

        if (!industry) {
            return record;
        }

        record[parentId] = ((industry.children ?? []) as IFlattenedCategory[]).flatMap(
            (child) => suggestedIndustries[child.id] ?? [],
        );
        return record;
    }, {});

    const suggestionsForIndustries = makeSuggestionsMap(selectedParentIds, industriesSuggestions);

    const result = [...suggestedParentIndustryIds];

    fillWithSuggestionsFromMap(result, suggestionsForSubIndustries);
    fillWithSuggestionsFromMap(result, suggestionsForIndustries);

    return result;
}
